import { LinkTo } from '@ember/routing';
import { Section, VStack } from 'tio-ui/components/layout';
import { Text } from 'tio-ui/components/typography';
import Chip from 'tio-reimagined/components/chip';
import PageHero from 'tio-reimagined/components/page-hero';
import StatCard from 'tio-reimagined/components/stat-card';
import ProgressBar from 'tio-reimagined/components/progress-bar';
import CircularProgress from 'tio-reimagined/components/circular-progress';
import PipelineFlow from 'tio-reimagined/components/pipeline-flow';
import TimelineTracker from 'tio-reimagined/components/timeline-tracker';
import InfoRow from 'tio-reimagined/components/info-row';
import {
  PhArrowLeft,
  PhArrowRight,
  PhBriefcase,
  PhCheckCircle,
  PhClock,
  PhCurrencyDollar,
  PhInfo,
  PhMagnifyingGlass,
  PhPaperPlaneTilt,
  PhStar,
  PhTarget,
  PhTrendUp,
  PhUsers,
} from 'ember-phosphor-icons';

const SAMPLE_PIPELINE = [
  { value: 124, label: 'Applied', color: 'violet' as const },
  { value: 89, label: 'Reviewed', color: 'teal' as const },
  { value: 42, label: 'Approved', color: 'green' as const },
  { value: 8, label: 'Completed', color: 'amber' as const },
];

const SAMPLE_TIMELINE = [
  {
    title: 'Application Submitted',
    description: 'Jan 15, 2026',
    state: 'completed' as const,
    date: '2026-01-15',
  },
  {
    title: 'Documents Reviewed',
    description: 'Jan 22, 2026',
    state: 'completed' as const,
    date: '2026-01-22',
  },
  { title: 'Approval Pending', description: 'In review by manager', state: 'active' as const },
  { title: 'Payment Processed', state: 'upcoming' as const },
];

const SAMPLE_TIMELINE_ICONS = [
  {
    title: 'Application Submitted',
    description: 'Jan 15, 2026',
    state: 'completed' as const,
    date: '2026-01-15',
    icon: PhPaperPlaneTilt,
  },
  {
    title: 'Under Review',
    description: 'Estimated 2-3 business days',
    state: 'active' as const,
    icon: PhMagnifyingGlass,
  },
  {
    title: 'Approved',
    state: 'upcoming' as const,
    icon: PhCheckCircle,
  },
  {
    title: 'Funds Disbursed',
    state: 'upcoming' as const,
    icon: PhCurrencyDollar,
  },
];

<template>
  {{! template-lint-disable no-bare-strings }}
  <VStack class="w-full min-w-0">
    <div class="mb-4">
      <LinkTo
        @route="authenticated.reimagined.components"
        class="inline-flex items-center gap-2 text-violet-600 dark:text-violet-400 hover:text-violet-800 dark:hover:text-violet-300 font-medium"
      >
        <PhArrowLeft @size={{18}} />
        Back to Components
      </LinkTo>
    </div>

    <PageHero
      @title="Data Display"
      @subtitle="StatCard, ProgressBar, CircularProgress, PipelineFlow, TimelineTracker, and InfoRow."
      @icon={{PhTrendUp}}
      @theme="amber"
      @decoration="dots"
    >
      <:actions>
        <Chip @intent="primary">6 Components</Chip>
      </:actions>
    </PageHero>

    <div class="space-y-8">

      {{! ========================================
            StatCard
            ======================================== }}
      <Section>
        <:header>
          <div class="flex items-center gap-2">
            <PhTrendUp @size={{20}} class="text-violet-600" />
            <span>StatCard</span>
            <Chip @size="sm" @intent="primary">2 Modes</Chip>
          </div>
        </:header>
        <:body>
          <Text @intent="muted" class="mb-4">
            Metric display with icon, value, label, and optional trend. Supports
            <strong>default</strong>
            mode (large card with icon circle) and
            <strong>compact</strong>
            mode (smaller, inline icon — formerly SummaryStatCard).
          </Text>

          <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4 space-y-4">
            <Text class="font-semibold text-gray-700 dark:text-gray-300 text-sm">Default Mode:</Text>
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                @icon={{PhCurrencyDollar}}
                @value="$5,250"
                @label="TA Available"
                @trend="up"
                @trendText="Renewed"
                @color="green"
              />
              <StatCard
                @icon={{PhCheckCircle}}
                @value="68"
                @suffix="%"
                @label="Plan Progress"
                @trend="up"
                @trendText="+12% this month"
                @color="violet"
              />
              <StatCard @icon={{PhBriefcase}} @value="8" @label="Open Roles" @color="blue" />
              <StatCard
                @label="Rating"
                @value="4.5"
                @suffix="/5"
                @icon={{PhStar}}
                @valueColor="amber"
                @color="amber"
              />
            </div>

            <Text class="font-semibold text-gray-700 dark:text-gray-300 text-sm">Compact Mode:</Text>
            <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
              <StatCard
                @compact={{true}}
                @label="In Pipeline"
                @value="124"
                @icon={{PhUsers}}
                @iconColor="violet"
                @valueColor="violet"
              />
              <StatCard
                @compact={{true}}
                @label="Approved"
                @value="42"
                @trend="up"
                @trendText="+8% QoQ"
                @valueColor="emerald"
              />
              <StatCard
                @compact={{true}}
                @label="Budget Used"
                @value="85"
                @suffix="%"
                @secondaryText="of annual allocation"
                @valueColor="amber"
              />
            </div>
          </div>

          <details class="border dark:border-gray-700 rounded-lg">
            <summary
              class="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Code Examples
            </summary>
            <pre
              class="p-4 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 overflow-x-auto border-t dark:border-gray-700"
            ><code>{{! Default mode }}
                &lt;StatCard @icon=&#123;&#123;PhCurrencyDollar&#125;&#125; @value="$5,250"
                @label="TA Available" @trend="up" @trendText="Renewed" @color="green" /&gt;

                {{! Compact mode (formerly SummaryStatCard) }}
                &lt;StatCard @compact=&#123;&#123;true&#125;&#125; @label="In Pipeline" @value="124"
                @icon=&#123;&#123;PhUsers&#125;&#125; @iconColor="violet" @valueColor="violet" /&gt;

                {{! With suffix and secondary text }}
                &lt;StatCard @compact=&#123;&#123;true&#125;&#125; @label="Budget Used" @value="85"
                @suffix="%" @secondaryText="of annual allocation" /&gt;</code></pre>
          </details>
        </:body>
      </Section>

      {{! ========================================
            ProgressBar + CircularProgress
            ======================================== }}
      <Section>
        <:header>
          <div class="flex items-center gap-2">
            <PhTarget @size={{20}} class="text-violet-600" />
            <span>ProgressBar &amp; CircularProgress</span>
          </div>
        </:header>
        <:body>
          <Text @intent="muted" class="mb-4">
            <strong>ProgressBar</strong>
            — horizontal bar with ARIA, 9 colors, 4 sizes, gradient option.
            <strong>CircularProgress</strong>
            — SVG ring with content slot.
          </Text>

          <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4 space-y-4">
            <Text class="font-semibold text-gray-700 dark:text-gray-300 text-sm">Progress Bars:</Text>
            <div class="space-y-3">
              <ProgressBar @value={{75}} @color="emerald" @size="md" />
              <ProgressBar @value={{50}} @color="violet" @size="sm" />
              <ProgressBar @value={{30}} @color="amber" @size="xs" @gradient={{true}} />
            </div>

            <Text class="font-semibold text-gray-700 dark:text-gray-300 text-sm">Circular Progress:</Text>
            <div class="flex items-end gap-8">
              <CircularProgress @value={{92}} @color="emerald" @size="lg">
                <span class="text-lg font-bold text-emerald-600 dark:text-emerald-400">92%</span>
              </CircularProgress>
              <CircularProgress @value={{65}} @color="violet" @size="md">
                <span class="text-sm font-bold text-violet-600 dark:text-violet-400">65%</span>
              </CircularProgress>
              <CircularProgress @value={{30}} @color="amber" @size="sm">
                <span class="text-xs font-bold text-amber-600 dark:text-amber-400">30%</span>
              </CircularProgress>
            </div>
          </div>

          <details class="border dark:border-gray-700 rounded-lg">
            <summary
              class="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Code Examples
            </summary>
            <pre
              class="p-4 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 overflow-x-auto border-t dark:border-gray-700"
            ><code>&lt;ProgressBar @value=&#123;&#123;75&#125;&#125; @color="emerald" @size="md"
                /&gt; &lt;ProgressBar @value=&#123;&#123;50&#125;&#125;
                @gradient=&#123;&#123;true&#125;&#125; /&gt; &lt;CircularProgress
                @value=&#123;&#123;92&#125;&#125; @color="emerald" @size="lg"&gt; &lt;span
                class="text-lg font-bold"&gt;92%&lt;/span&gt; &lt;/CircularProgress&gt;</code></pre>
          </details>
        </:body>
      </Section>

      {{! ========================================
            PipelineFlow
            ======================================== }}
      <Section>
        <:header>
          <div class="flex items-center gap-2">
            <PhArrowRight @size={{20}} class="text-violet-600" />
            <span>PipelineFlow</span>
          </div>
        </:header>
        <:body>
          <Text @intent="muted" class="mb-4">
            Horizontal flow visualization with stages connected by arrows. Uses PipelineStage
            internally. Supports equal-width and wrapping modes.
          </Text>

          <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4">
            <PipelineFlow @stages={{SAMPLE_PIPELINE}} @size="md" />
          </div>

          <details class="border dark:border-gray-700 rounded-lg">
            <summary
              class="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Code Examples
            </summary>
            <pre
              class="p-4 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 overflow-x-auto border-t dark:border-gray-700"
            ><code>const stages = [ { value: 124, label: 'Applied', color: 'violet' }, { value: 89,
                label: 'Reviewed', color: 'teal' }, { value: 42, label: 'Approved', color: 'green'
                }, ]; &lt;PipelineFlow @stages=&#123;&#123;stages&#125;&#125; @size="md" /&gt;</code></pre>
          </details>
        </:body>
      </Section>

      {{! ========================================
            TimelineTracker
            ======================================== }}
      <Section>
        <:header>
          <div class="flex items-center gap-2">
            <PhClock @size={{20}} class="text-violet-600" />
            <span>TimelineTracker</span>
            <Chip @size="sm" @intent="primary">Icons</Chip>
          </div>
        </:header>
        <:body>
          <Text @intent="muted" class="mb-4">
            Vertical timeline with completed/active/upcoming state markers connected by lines.
            Supports compact mode for tighter spacing. Each step can optionally include a custom
            icon component that renders inside the marker.
          </Text>

          <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4 space-y-6">
            <div>
              <Text class="font-semibold text-gray-700 dark:text-gray-300 text-sm mb-3">Default
                markers:</Text>
              <div class="max-w-md">
                <TimelineTracker @steps={{SAMPLE_TIMELINE}} />
              </div>
            </div>

            <div>
              <Text class="font-semibold text-gray-700 dark:text-gray-300 text-sm mb-3">With custom
                icons:</Text>
              <div class="max-w-md">
                <TimelineTracker @steps={{SAMPLE_TIMELINE_ICONS}} />
              </div>
            </div>
          </div>

          <details class="border dark:border-gray-700 rounded-lg">
            <summary
              class="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Code Examples
            </summary>
            <pre
              class="p-4 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 overflow-x-auto border-t dark:border-gray-700"
            ><code>{{! Default markers }}
                const steps = [ { title: 'Submitted', state: 'completed', date: '2026-01-15' }, {
                title: 'In Review', description: 'Manager review', state: 'active' }, { title:
                'Approved', state: 'upcoming' }, ]; &lt;TimelineTracker
                @steps=&#123;&#123;steps&#125;&#125; /&gt;

                {{! With custom icons per step }}
                const stepsWithIcons = [ { title: 'Submitted', state: 'completed', icon:
                PhPaperPlaneTilt }, { title: 'Under Review', state: 'active', icon:
                PhMagnifyingGlass }, { title: 'Approved', state: 'upcoming', icon: PhCheckCircle },
                ]; &lt;TimelineTracker @steps=&#123;&#123;stepsWithIcons&#125;&#125; /&gt;</code></pre>
          </details>
        </:body>
      </Section>

      {{! ========================================
            InfoRow
            ======================================== }}
      <Section>
        <:header>
          <div class="flex items-center gap-2">
            <PhInfo @size={{20}} class="text-violet-600" />
            <span>InfoRow</span>
          </div>
        </:header>
        <:body>
          <Text @intent="muted" class="mb-4">
            Simple label/value row for detail panels and cards. Supports block fallback for rich
            value content.
          </Text>

          <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4">
            <div class="divide-y dark:divide-gray-700">
              <InfoRow @label="Program" @value="MBA Healthcare Administration" />
              <InfoRow @label="Institution" @value="University of Phoenix" />
              <InfoRow @label="Status">
                <Chip @intent="success" @size="sm">Active</Chip>
              </InfoRow>
              <InfoRow @label="Amount" @value="$2,400" @size="sm" />
            </div>
          </div>

          <details class="border dark:border-gray-700 rounded-lg">
            <summary
              class="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Code Examples
            </summary>
            <pre
              class="p-4 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 overflow-x-auto border-t dark:border-gray-700"
            ><code>&lt;InfoRow @label="Program" @value="MBA Healthcare" /&gt;

                {{! Block fallback for rich content }}
                &lt;InfoRow @label="Status"&gt; &lt;Chip @intent="success"&gt;Active&lt;/Chip&gt;
                &lt;/InfoRow&gt;</code></pre>
          </details>
        </:body>
      </Section>
    </div>
  </VStack>
</template>
